import { useFullPageSheet } from "@base/ui/FullPageSheet";
import { Button, TextField } from "@buildblockinc/buildblock-design-system";
import type { DailyLogForm } from "@features/write-daily-log/model/form";
import type { ProjectContractorItem } from "@features/write-daily-log/model/projectContractorsSchema";
import type { ProjectTradeItem } from "@features/write-daily-log/model/projectTradesSchema";
import { useParams } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useState } from "react";
import { ContractorSearchSheet } from "./ContractorSearchSheet";
import { EquipmentSearchSheet } from "./EquipmentSearchSheet";
import { DecimalNumberField } from "./NumberField";

interface EquipmentStepProps {
  form: DailyLogForm;
}

export function EquipmentStep({ form }: EquipmentStepProps) {
  const { projectId } = useParams({ strict: false });
  const contractorSheet = useFullPageSheet();
  const equipmentSheet = useFullPageSheet();
  const [activeContractorIndex, setActiveContractorIndex] = useState<number | null>(null);

  return (
    <form.Field name="equipment_contractors" mode="array">
      {(contractorsField) => {
        const contractors = contractorsField.state.value;
        const activeEquipments =
          activeContractorIndex !== null ? (contractors[activeContractorIndex]?.equipments ?? []) : [];

        return (
          <>
            <Button type="button" variant="outline" color="secondary" className="w-full" onClick={contractorSheet.open}>
              Add / Select Contractor
            </Button>

            <div className="flex flex-col gap-y-4 mt-4">
              {contractors.map((contractor, contractorIndex) => (
                <div
                  key={contractor.id}
                  className="flex flex-col gap-y-4 p-4 bg-white border border-blue-500 rounded-8"
                >
                  <p className="text-gray-900 text-subtitle2">{contractor.name}</p>

                  <form.Field name={`equipment_contractors[${contractorIndex}].equipments`} mode="array">
                    {(equipmentsField) => (
                      <ul className="flex flex-col gap-y-2">
                        {(equipmentsField.state.value ?? []).map((equipment, equipmentIndex) => (
                          <li key={equipment.id} className="flex gap-x-3 items-center p-4 bg-blue-10 rounded-8">
                            <span className="flex-1 min-w-0 text-gray-900 truncate text-label2">{equipment.name}</span>
                            <form.Field
                              name={`equipment_contractors[${contractorIndex}].equipments[${equipmentIndex}].quantity`}
                            >
                              {(qtyField) => (
                                <TextField className="w-[55px]">
                                  <DecimalNumberField
                                    value={qtyField.state.value}
                                    onChange={qtyField.handleChange}
                                    placeholder="0"
                                  />
                                </TextField>
                              )}
                            </form.Field>
                            <button
                              type="button"
                              onClick={() =>
                                equipmentsField.handleChange(
                                  equipmentsField.state.value.filter((_, i) => i !== equipmentIndex),
                                )
                              }
                              aria-label={`Remove ${equipment.name}`}
                            >
                              <X width={20} height={20} className="text-gray-500 cursor-pointer shrink-0" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </form.Field>

                  <Button
                    type="button"
                    variant="outline"
                    color="secondary"
                    className="w-full"
                    onClick={() => {
                      setActiveContractorIndex(contractorIndex);
                      equipmentSheet.open();
                    }}
                  >
                    Add
                  </Button>
                </div>
              ))}
            </div>

            <ContractorSearchSheet
              projectId={projectId ?? ""}
              isOpen={contractorSheet.isOpen}
              onClose={contractorSheet.close}
              currentContractors={contractors.map((c) => ({ id: c.id, name: c.name }))}
              onPickCatalogItem={(item: ProjectContractorItem) => {
                if (contractorsField.state.value.some((c) => c.id === item.id)) return;
                contractorsField.handleChange([
                  ...contractorsField.state.value,
                  { id: item.id, name: item.name, equipments: [] },
                ]);
              }}
              onRemove={(id: number) => {
                contractorsField.handleChange(contractorsField.state.value.filter((c) => c.id !== id));
              }}
            />

            <EquipmentSearchSheet
              projectId={projectId ?? ""}
              isOpen={equipmentSheet.isOpen}
              onClose={equipmentSheet.close}
              currentEquipments={activeEquipments}
              onPickCatalogItem={(item: ProjectTradeItem) => {
                if (activeContractorIndex === null) return;
                const current = contractorsField.state.value;
                contractorsField.handleChange(
                  current.map((c, i) => {
                    if (i !== activeContractorIndex) return c;
                    if (c.equipments.some((e) => e.name === item.name)) return c;
                    return {
                      ...c,
                      equipments: [...c.equipments, { id: String(item.id), name: item.name, quantity: 0 }],
                    };
                  }),
                );
              }}
              onRemove={(id: string) => {
                if (activeContractorIndex === null) return;
                const current = contractorsField.state.value;
                contractorsField.handleChange(
                  current.map((c, i) => {
                    if (i !== activeContractorIndex) return c;
                    return { ...c, equipments: c.equipments.filter((e) => e.id !== id) };
                  }),
                );
              }}
            />
          </>
        );
      }}
    </form.Field>
  );
}
